import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Share2, Link2, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { API, http } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Logo } from "@/components/brand/Logo";

const shareUrl = (slug) => `${API}/share/recipe/${slug}`;

const ShareButtons = ({ recipe }) => {
  const url = shareUrl(recipe.slug);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste it anywhere");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: recipe.name, text: `${recipe.name} — a cold-press recipe by Kathryn`, url });
      } catch {
        /* user cancelled */
      }
    } else copy();
  };
  return (
    <div className="flex flex-wrap gap-3">
      <button data-testid="recipe-share-button" onClick={share} className="inline-flex items-center gap-2 rounded-full bg-[#181615] px-6 py-3 text-sm font-semibold text-[#FDFBF7] transition-[background-color] duration-300 hover:bg-[#C85A32]"><Share2 size={16} /> Share recipe</button>
      <button data-testid="recipe-copy-link-button" onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-[#181615]/20 px-6 py-3 text-sm font-medium hover:bg-white"><Link2 size={16} /> Copy link</button>
    </div>
  );
};

export default function RecipePage() {
  const { slug } = useParams();
  const { openCheckout } = useStore();
  const [r, setR] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    http.get(`/recipes/featured/${slug}`).then((res) => { setR(res.data); setState("ok"); }).catch(() => setState("error"));
  }, [slug]);
  useEffect(() => {
    if (r?.name) document.title = `${r.name} — Golden Hour Press`;
  }, [r]);

  return (
    <main data-testid="recipe-page" className="grain relative min-h-screen bg-[#F2EAE1]">
      <div className="relative mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <div className="flex items-center justify-between">
          <Logo />
          <a href="/#almanac" data-testid="recipe-back-link" className="flex items-center gap-2 text-sm text-[#4A4543] hover:text-[#C85A32]"><ArrowLeft size={16} /> All recipes</a>
        </div>
        {state === "loading" && <Loader2 className="mt-32 animate-spin text-[#C85A32]" />}
        {state === "error" && <p data-testid="recipe-not-found" className="font-display mt-32 text-3xl font-light">That recipe isn't available.</p>}
        {state === "ok" && r && (
          <>
            <img data-testid="recipe-hero-image" src={r.image} alt={`${r.name} by Kathryn, Juice Alchemist`} className="mt-10 aspect-[1200/630] w-full rounded-[28px] object-cover shadow-[0_30px_60px_-30px_rgba(120,60,20,0.4)]" />
            <div className="mt-10 grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="eyebrow text-[#C85A32]">{r.chapter} · {r.meta}</p>
                <h1 data-testid="recipe-title" className="font-display mt-3 text-5xl font-light leading-none tracking-tight sm:text-6xl">{r.name}</h1>
                <p className="font-display mt-3 text-xl italic text-[#4A4543]">by Kathryn, Juice Alchemist</p>
                <h2 className="eyebrow mt-10 text-[#6E6661]">Method</h2>
                <p data-testid="recipe-method" className="mt-3 text-base leading-relaxed text-[#181615] md:text-lg">{r.method}</p>
                <p className="mt-4 text-sm text-[#5E7153]">{r.note}</p>
                <div className="mt-10"><ShareButtons recipe={r} /></div>
              </div>
              <div className="lg:col-span-5">
                <div className="rounded-[24px] border border-[#E3D9CD] bg-[#FDFBF7] p-7">
                  <h2 className="eyebrow text-[#6E6661]">Ingredients</h2>
                  <ul data-testid="recipe-ingredients" className="mt-4 space-y-3">
                    {(r.ingredients || []).map((i) => <li key={i} className="flex gap-3 border-b border-[#E3D9CD] pb-3 text-sm last:border-0"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#C85A32]" />{i}</li>)}
                  </ul>
                </div>
                <div className="mt-5 rounded-[24px] bg-[#181615] p-7 text-[#F2EAE1]">
                  <p className="font-display text-2xl italic">Want all 12 recipes?</p>
                  <p className="mt-2 text-sm text-[#C9BFB5]">The full Recipe Almanac comes free with every Slowpour 01.</p>
                  <button data-testid="recipe-buy-button" onClick={openCheckout} className="mt-5 rounded-full bg-[#E0A030] px-6 py-3 text-sm font-semibold text-[#181615] hover:bg-[#F2EAE1]">Get the juicer — $250</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
